import { KeyboardArrowRight } from "@mui/icons-material";
import { Button, Checkbox, Chip, Tooltip, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import { P } from "const/permissions";
import { usePermission } from "hooks/usePermissions";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ENTRY_STATUS_LABELS, groupCommissions, rowRole } from "utils/commissionGrouping";
import PayoutDialog, { StatusChipMini, payoutToast } from "./PayoutDialog";
import { approveCommissions, createPayout, listCommissions } from "../referralPartnersApi";
import {
  approvableRowIds,
  canApproveEntry,
  commissionTotals,
  entryBlockedReason,
  payableRowIds,
  payoutBlockedReason,
  selectionSummary,
} from "./ledgerSelection";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "ACCRUED", label: "Awaiting approval" },
  { value: "APPROVED", label: "Approved" },
  { value: "PAID", label: "Paid" },
  { value: "CANCELLED", label: "Cancelled" },
];

/** Select | entry | status | payout ref | net — fixed, so opening an entry never shifts columns. */
const GRID =
  "grid grid-cols-[2.25rem_minmax(0,1fr)_7.5rem] sm:grid-cols-[2.25rem_minmax(0,1fr)_9.5rem_8rem_7.5rem] items-center gap-x-3";
const PAGE_SIZE = 50;
// The summary adds up every row, not just the page on screen; this caps that one request.
const SUMMARY_SIZE = 1000;

const money = (value) =>
  value === null || value === undefined
    ? "—"
    : `$${Number(value).toLocaleString("en-AU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

/**
 * The ledger, grouped the way the money is actually settled: a client holds entries, an entry is a
 * source amount plus every correction made to it, and an entry is approved and paid as one thing.
 */
const CommissionLedgerPanel = ({ partnerId }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canApprove = usePermission(P.REFERRAL.COMMISSION_APPROVE);
  const canPay = usePermission(P.REFERRAL.PAYOUT_CREATE);
  // The status filter rides in the URL with the tab, so a refresh keeps it.
  const [searchParams, setSearchParams] = useSearchParams();
  const status = STATUS_FILTERS.some((option) => option.value === searchParams.get("status"))
    ? searchParams.get("status")
    : "";
  const [page, setPage] = useState(0);
  const setStatus = (next) =>
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (next) params.set("status", next);
        else params.delete("status");
        return params;
      },
      { replace: true }
    );
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [approveAsk, setApproveAsk] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);

  const params = useMemo(
    () => ({ page, size: PAGE_SIZE, ...(status ? { status } : {}) }),
    [page, status]
  );

  const { data, isLoading, isError, refetch } = useQuery(
    ["referral-commissions", partnerId, params],
    () => listCommissions(partnerId, params),
    { keepPreviousData: true, refetchOnMount: true }
  );
  const totalPages = data?.totalPages ?? 0;

  // Unfiltered, so the totals stay the same whichever status or page is showing.
  const summaryQuery = useQuery(
    ["referral-commissions", partnerId, "summary"],
    () => listCommissions(partnerId, { page: 0, size: SUMMARY_SIZE }),
    { refetchOnMount: true }
  );
  const totals = useMemo(
    () => commissionTotals(summaryQuery.data?.content || []),
    [summaryQuery.data]
  );
  const summaryPartial = (summaryQuery.data?.totalItems ?? 0) > SUMMARY_SIZE;

  const refresh = () => {
    setSelectedEntries([]);
    refetch();
    queryClient.invalidateQueries(["referral-commissions", partnerId, "summary"]);
    queryClient.invalidateQueries(["referral-partners"]);
    queryClient.invalidateQueries(["referral-overview"]);
    queryClient.invalidateQueries(["referral-overview", "partner", partnerId]);
  };

  // Selection lives on the visible page only: entries elsewhere cannot be approved or paid.
  const changePage = (next) => {
    setSelectedEntries([]);
    setPage(next);
  };

  const approveMutation = useMutation((ids) => approveCommissions(ids), {
    onSuccess: (count) => {
      toast.success(`${count} row${count === 1 ? "" : "s"} approved`);
      setApproveAsk(false);
      refresh();
    },
    onError: (err) =>
      toast.error(
        err?.response?.data?.errorMessage || err?.response?.data?.message || "Could not approve"
      ),
  });

  const payoutMutation = useMutation((payload) => createPayout(partnerId, payload), {
    onSuccess: () => {
      toast.success("Payout recorded");
      setPayoutOpen(false);
      refresh();
    },
    onError: payoutToast,
  });

  const clients = useMemo(() => groupCommissions(data?.content || []), [data]);
  const allEntries = useMemo(() => clients.flatMap((client) => client.entries), [clients]);
  const selected = allEntries.filter((entry) => selectedEntries.includes(entry.entryId));
  const summary = selectionSummary(selected);
  const payoutBlocked = payoutBlockedReason(selected);

  const toggleEntry = (entryId) =>
    setSelectedEntries((prev) =>
      prev.includes(entryId) ? prev.filter((id) => id !== entryId) : [...prev, entryId]
    );

  // Select or clear every entry of one client that can be acted on.
  const toggleClient = (client) => {
    const ids = client.entries.filter(isSelectable).map((entry) => entry.entryId);
    const allOn = ids.length > 0 && ids.every((id) => selectedEntries.includes(id));
    setSelectedEntries((prev) =>
      allOn ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])]
    );
  };

  const toggleExpanded = (entryId) =>
    setExpanded((prev) =>
      prev.includes(entryId) ? prev.filter((id) => id !== entryId) : [...prev, entryId]
    );

  return (
    <section className="overflow-hidden rounded-xl border border-app-border bg-app-surface">
      <header className="border-b border-app-border px-4 py-3.5">
        <Typography variant="h6" sx={{ m: 0, fontSize: 17, fontWeight: 700 }}>
          Commission
        </Typography>

        <div className="mt-3 grid grid-cols-2 gap-2 lg:grid-cols-4">
          {[
            {
              key: "ACCRUED",
              label: "Awaiting approval",
              value: totals.awaiting,
              hint: totals.entriesAwaiting
                ? `${totals.entriesAwaiting} entr${totals.entriesAwaiting === 1 ? "y" : "ies"}`
                : "Nothing pending",
              tone: totals.awaiting ? "border-sky-200 bg-sky-50 text-sky-900" : "",
            },
            {
              key: "APPROVED",
              label: "Approved, to pay",
              value: totals.approved,
              hint: totals.approved ? "Ready for a payout" : "Nothing to pay",
              tone: totals.approved ? "border-amber-200 bg-amber-50 text-amber-900" : "",
            },
            {
              key: "PAID",
              label: "Paid",
              value: totals.paid,
              hint: "Sent to the partner",
              tone: "",
            },
            {
              key: "",
              label: "Total earned",
              value: totals.total,
              hint: summaryPartial ? `Latest ${SUMMARY_SIZE} rows` : "Excludes cancelled",
              tone: "",
            },
          ].map((tile) => (
            <button
              key={tile.label}
              type="button"
              onClick={() => {
                setStatus(tile.key);
                setSelectedEntries([]);
                setPage(0);
              }}
              title={`Show ${tile.key ? tile.label.toLowerCase() : "everything"}`}
              className={[
                "rounded-lg border px-3 py-2 text-left cursor-pointer transition-colors hover:border-primary",
                tile.tone || "border-app-border bg-app-surface text-app-text",
                status === tile.key ? "ring-2 ring-primary ring-offset-1" : "",
              ].join(" ")}
            >
              <span className="block text-[11px] font-semibold uppercase tracking-wide opacity-70">
                {tile.label}
              </span>
              <span className="mt-0.5 block text-base font-bold tabular-nums">
                {summaryQuery.isLoading ? "…" : money(tile.value)}
              </span>
              <span className="block text-[11px] opacity-70">{tile.hint}</span>
            </button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap gap-2" role="tablist" aria-label="Filter by status">
          {STATUS_FILTERS.map((option) => {
            const active = status === option.value;
            return (
              <button
                key={option.value || "all"}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  setStatus(option.value);
                  setSelectedEntries([]);
                  setPage(0);
                }}
                className={[
                  "rounded-full border px-3 py-1 text-xs font-semibold cursor-pointer transition-colors",
                  active
                    ? "border-primary bg-primary text-white"
                    : "border-app-border bg-app-surface text-app-text-secondary hover:text-app-text",
                ].join(" ")}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </header>
      {/* {selected.length > 0 && (canApprove || canPay) ? ( */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-app-border bg-app-surface-muted px-4 py-2.5">
        <div className="min-w-0 text-sm text-app-text">
          <strong>
            {selected.length} entr{selected.length === 1 ? "y" : "ies"}
          </strong>{" "}
          selected · <strong className="tabular-nums">{money(summary.net)}</strong> net
          {payoutBlocked ? (
            <span className="block text-xs text-app-text-muted">{payoutBlocked}</span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="small" onClick={() => setSelectedEntries([])}>
            Clear
          </Button>
          {canApprove ? (
            <Button
              size="small"
              variant="outlined"
              disabled={!summary.canApprove || approveMutation.isLoading}
              onClick={() => setApproveAsk(true)}
            >
              Approve
            </Button>
          ) : null}
          {canPay ? (
            <Tooltip title={payoutBlocked || ""}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  disabled={Boolean(payoutBlocked) || payoutMutation.isLoading}
                  onClick={() => setPayoutOpen(true)}
                >
                  Record payout
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </div>
      </div>
      {/* // ) : null} */}
      {isError ? (
        <Typography color="error" sx={{ p: 2 }}>
          Could not load commission.
        </Typography>
      ) : !isLoading && clients.length === 0 ? (
        <p className="m-0 px-4 py-10 text-center text-sm text-app-text-muted">
          {status
            ? "Nothing under this status."
            : "No commission yet. Amounts appear here once a referral amount is entered against one of this partner's clients."}
        </p>
      ) : (
        <div className="p-3 sm:p-4">
          <div
            className={`${GRID} px-3 pb-2 text-[11px] font-semibold uppercase tracking-wide text-app-text-muted`}
          >
            <span />
            <span>Client / entry</span>
            <span className="hidden sm:block">Status</span>
            <span className="hidden sm:block">Payout ref</span>
            <span className="text-right">Net</span>
          </div>
          <ul className="m-0 list-none space-y-3 p-0">
            {clients.map((client) => (
              <ClientBlock
                key={client.clientId ?? client.clientName}
                client={client}
                expanded={expanded}
                selectedEntries={selectedEntries}
                selectable={canApprove || canPay}
                onToggleClient={() => toggleClient(client)}
                onToggleEntry={toggleEntry}
                onToggleExpanded={toggleExpanded}
                onOpenClient={() => navigate(`/admin/client/view/${client.clientId}?tab=personal`)}
              />
            ))}
          </ul>
          {totalPages > 1 ? (
            <div className="mt-4 flex items-center justify-between gap-3 border-t border-app-border pt-3">
              <Button size="small" disabled={page === 0} onClick={() => changePage(page - 1)}>
                Previous
              </Button>
              <Typography variant="body2" color="text.secondary">
                Page {page + 1} of {totalPages}
              </Typography>
              <Button
                size="small"
                disabled={page + 1 >= totalPages}
                onClick={() => changePage(page + 1)}
              >
                Next
              </Button>
            </div>
          ) : null}
        </div>
      )}
      <ConfirmDialog
        open={approveAsk}
        title="Approve these entries?"
        message={`${
          approvableRowIds(selected).length
        } row(s) will be frozen at their amounts. Later changes to the account become corrections on the same entry.`}
        confirmLabel="Approve"
        loading={approveMutation.isLoading}
        onConfirm={() => approveMutation.mutate(approvableRowIds(selected))}
        onCancel={() => setApproveAsk(false)}
      />
      <PayoutDialog
        open={payoutOpen}
        rowCount={payableRowIds(selected).length}
        selectedTotal={summary.net}
        submitting={payoutMutation.isLoading}
        onClose={() => setPayoutOpen(false)}
        onSubmit={(payload) =>
          payoutMutation.mutate({ ...payload, commissionIds: payableRowIds(selected) })
        }
      />
    </section>
  );
};

/** An entry can be ticked when it can be approved or paid; the reason shows when it cannot. */
const isSelectable = (entry) => entryBlockedReason(entry) === null || canApproveEntry(entry);

/** The account of a row: why it exists, who raised it, who signed it off. */
const RowTrail = ({ row }) => {
  const parts = [];
  if (row.raisedByName) parts.push(`Raised by ${row.raisedByName}`);
  if (row.approvedByName) {
    parts.push(
      `Approved by ${row.approvedByName}${
        row.approvedAt ? ` on ${new Date(row.approvedAt).toLocaleDateString()}` : ""
      }`
    );
  }
  if (!row.note && parts.length === 0) return null;
  return (
    <div className="mt-0.5 text-[11px] text-app-text-muted opacity-80">
      {row.note ? <div>{row.note}</div> : null}
      {parts.length > 0 ? <div>{parts.join(" · ")}</div> : null}
    </div>
  );
};

const ClientBlock = ({
  client,
  expanded,
  selectedEntries,
  selectable,
  onToggleClient,
  onToggleEntry,
  onToggleExpanded,
  onOpenClient,
}) => {
  const selectableIds = client.entries.filter(isSelectable).map((entry) => entry.entryId);
  const chosen = selectableIds.filter((id) => selectedEntries.includes(id)).length;
  return (
    <li className="overflow-hidden rounded-xl border border-app-border">
      <div className={`${GRID} bg-app-surface-muted px-3 py-2.5`}>
        <span>
          {selectable && selectableIds.length ? (
            <Checkbox
              size="small"
              checked={chosen > 0 && chosen === selectableIds.length}
              indeterminate={chosen > 0 && chosen < selectableIds.length}
              onChange={onToggleClient}
              inputProps={{ "aria-label": `Select every entry for ${client.clientName}` }}
              sx={{ p: 0.5 }}
            />
          ) : null}
        </span>
        <div className="min-w-0 sm:col-span-3">
          <button
            type="button"
            onClick={onOpenClient}
            className="truncate border-0 bg-transparent p-0 text-left text-sm font-semibold text-primary cursor-pointer hover:underline"
          >
            {client.clientName}
          </button>
          <span className="ml-2 text-xs text-app-text-muted">
            {client.entries.length} entr{client.entries.length === 1 ? "y" : "ies"}
          </span>
        </div>
        <span className="text-right text-sm font-bold tabular-nums text-app-text">
          {money(client.net)}
        </span>
      </div>

      <ul className="m-0 list-none divide-y divide-app-border p-0">
        {client.entries.map((entry) => {
          const isOpen = expanded.includes(entry.entryId);
          const blocked = entryBlockedReason(entry);
          const canTick = selectable && isSelectable(entry);
          const payoutReference = entry.rows.map((row) => row.payoutReference).find(Boolean);
          return (
            <li key={entry.entryId}>
              <div className={`${GRID} px-3 py-2 ${isOpen ? "bg-app-surface-muted" : ""}`}>
                <span>
                  {selectable ? (
                    <Tooltip title={canTick ? "" : blocked || ""}>
                      <span>
                        <Checkbox
                          size="small"
                          checked={selectedEntries.includes(entry.entryId)}
                          disabled={!canTick}
                          onChange={() => onToggleEntry(entry.entryId)}
                          inputProps={{ "aria-label": `Select ${entry.moduleLabel}` }}
                          sx={{ p: 0.5 }}
                        />
                      </span>
                    </Tooltip>
                  ) : null}
                </span>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-label={`${isOpen ? "Hide" : "Show"} the ${entry.rows.length} row${
                    entry.rows.length === 1 ? "" : "s"
                  } behind ${entry.moduleLabel}`}
                  onClick={() => onToggleExpanded(entry.entryId)}
                  className="flex min-w-0 items-center gap-1.5 border-0 bg-transparent p-0 text-left cursor-pointer"
                >
                  <KeyboardArrowRight
                    sx={{ fontSize: 18 }}
                    className={`shrink-0 text-app-text-muted transition-transform duration-200 ${
                      isOpen ? "rotate-90" : ""
                    }`}
                  />
                  <span className="truncate text-sm text-app-text">{entry.moduleLabel}</span>
                  {entry.correctionCount > 0 ? (
                    <Chip
                      size="small"
                      label={`${entry.correctionCount} correction${
                        entry.correctionCount === 1 ? "" : "s"
                      }`}
                      sx={{ height: 20, fontSize: 11 }}
                    />
                  ) : null}
                </button>
                <span className="hidden sm:block">
                  <StatusChipMini status={entry.status} label={ENTRY_STATUS_LABELS[entry.status]} />
                </span>
                <span className="hidden truncate text-xs text-app-text-muted sm:block">
                  {payoutReference || "—"}
                </span>
                <span className="text-right text-sm font-semibold tabular-nums text-app-text">
                  {money(entry.net)}
                </span>
              </div>

              {isOpen ? (
                <ol className="m-0 list-none border-t border-app-border bg-app-surface-muted py-1 pl-0">
                  {entry.rows.map((row, index) => (
                    <li key={row.id} className={`${GRID} px-3 py-1.5 text-xs`}>
                      <span />
                      <div className="min-w-0 border-l-2 border-app-border pl-3 text-app-text-secondary">
                        <div className="font-medium text-app-text">
                          {rowRole(row, index)}
                          {row.earnedAt ? (
                            <span className="font-normal text-app-text-muted">
                              {" "}
                              · {new Date(row.earnedAt).toLocaleDateString()}
                            </span>
                          ) : null}
                        </div>
                        <RowTrail row={row} />
                      </div>
                      <span className="hidden sm:block">
                        <StatusChipMini status={row.status} />
                      </span>
                      <span className="hidden truncate text-app-text-muted sm:block">
                        {row.payoutReference || "—"}
                      </span>
                      <span
                        className={`text-right tabular-nums ${
                          Number(row.amount) < 0 ? "text-rose-600" : "text-app-text"
                        }`}
                      >
                        {money(row.amount)}
                      </span>
                    </li>
                  ))}
                </ol>
              ) : null}
            </li>
          );
        })}
      </ul>
    </li>
  );
};

export default CommissionLedgerPanel;
